import axios from 'axios';

// Deployment is split across origins on Railway: frontend (static) at
// engine.filmbaker.com, backend at its own Railway URL. REACT_APP_BACKEND_URL
// is baked in at build time and MUST be honoured. Fall back to the runtime
// origin only when the env is missing (e.g. local tooling).
export const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').trim()
  || (typeof window !== 'undefined' ? window.location.origin : '');

const API = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  timeout: 30000,
});

// Add Authorization header from localStorage (mobile Safari fallback)
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401 — NO redirect loops
API.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    // Skip interceptor for auth endpoints
    const isAuthEndpoint = original.url?.includes('/auth/');
    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      // Only attempt refresh if we actually have a refresh token
      if (refreshToken) {
        try {
          const { data } = await API.post('/auth/refresh', { refresh_token: refreshToken });
          if (data.access_token) {
            localStorage.setItem('access_token', data.access_token);
          }
          return API(original);
        } catch {
          // Refresh failed — clear tokens, let the app handle the redirect via React Router
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
    }
    return Promise.reject(error);
  }
);

export function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg).join(', ');
  return String(detail);
}

export default API;
